import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import BarChartIcon from '@mui/icons-material/BarChart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Mock data for plans
const initialPlans = [
  {
    id: 'basic',
    name: 'Basic',
    description: 'Essential features for beginners',
    monthlyPrice: 29,
    annualPrice: 290,
    isPopular: false,
    features: [
      { id: 1, name: 'Active Strategies', value: '3', included: true },
      { id: 2, name: 'Technical Analysis Tools', value: 'Basic', included: true },
      { id: 3, name: 'Backtesting', value: 'Limited', included: true },
      { id: 4, name: 'Email Support', value: 'Standard', included: true },
      { id: 5, name: 'AI Trading Signals', value: '', included: false },
      { id: 6, name: 'Advanced Risk Management', value: '', included: false },
      { id: 7, name: 'API Access', value: '', included: false },
      { id: 8, name: 'Phone Support', value: '', included: false },
    ],
    active: true,
    users: 523,
    revenue: 15167
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Advanced features for serious traders',
    monthlyPrice: 79,
    annualPrice: 790,
    isPopular: true,
    features: [
      { id: 1, name: 'Active Strategies', value: '10', included: true },
      { id: 2, name: 'Technical Analysis Tools', value: 'Advanced', included: true },
      { id: 3, name: 'Backtesting', value: 'Unlimited', included: true },
      { id: 4, name: 'Email Support', value: 'Priority', included: true },
      { id: 5, name: 'AI Trading Signals', value: 'Included', included: true },
      { id: 6, name: 'Advanced Risk Management', value: 'Included', included: true },
      { id: 7, name: 'API Access', value: '', included: false },
      { id: 8, name: 'Phone Support', value: '', included: false },
    ],
    active: true,
    users: 612,
    revenue: 48348
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Maximum power for professional traders',
    monthlyPrice: 199,
    annualPrice: 1990,
    isPopular: false,
    features: [
      { id: 1, name: 'Active Strategies', value: 'Unlimited', included: true },
      { id: 2, name: 'Technical Analysis Tools', value: 'Advanced', included: true },
      { id: 3, name: 'Backtesting', value: 'Unlimited', included: true },
      { id: 4, name: 'Email Support', value: '24/7 Priority', included: true },
      { id: 5, name: 'AI Trading Signals', value: 'Advanced', included: true },
      { id: 6, name: 'Advanced Risk Management', value: 'Advanced', included: true },
      { id: 7, name: 'API Access', value: 'Full Access', included: true },
      { id: 8, name: 'Phone Support', value: 'Dedicated Manager', included: true },
    ],
    active: true,
    users: 113,
    revenue: 22487
  }
];

// Mock data for available features
const availableFeatures = [
  { id: 1, name: 'Active Strategies', type: 'number-or-text' },
  { id: 2, name: 'Technical Analysis Tools', type: 'text' },
  { id: 3, name: 'Backtesting', type: 'text' },
  { id: 4, name: 'Email Support', type: 'text' },
  { id: 5, name: 'AI Trading Signals', type: 'text' },
  { id: 6, name: 'Advanced Risk Management', type: 'text' },
  { id: 7, name: 'API Access', type: 'text' },
  { id: 8, name: 'Phone Support', type: 'text' },
  { id: 9, name: 'Dedicated Account Manager', type: 'boolean' },
  { id: 10, name: 'White-label Solution', type: 'boolean' }
];

function AdminPlans() {
  const [plans, setPlans] = useState(initialPlans);
  const [openPlanDialog, setOpenPlanDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openFeaturesDialog, setOpenFeaturesDialog] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [editMode, setEditMode] = useState(false);
  
  // Handle opening the plan dialog for adding or editing
  const handleOpenPlanDialog = (plan = null) => {
    if (plan) {
      setCurrentPlan({ ...plan });
      setEditMode(true);
    } else {
      setCurrentPlan({
        id: '',
        name: '',
        description: '',
        monthlyPrice: '',
        annualPrice: '',
        isPopular: false,
        features: [],
        active: true
      });
      setEditMode(false);
    }
    setOpenPlanDialog(true);
  };
  
  // Handle saving a plan
  const handleSavePlan = () => {
    if (editMode) {
      // Update existing plan
      setPlans(plans.map(p => p.id === currentPlan.id ? currentPlan : p));
      setSuccessMessage(`Plan "${currentPlan.name}" has been updated successfully.`);
    } else {
      // Add new plan
      const newPlan = {
        ...currentPlan,
        id: currentPlan.name.toLowerCase().replace(/\s+/g, '-'),
        users: 0,
        revenue: 0
      };
      setPlans([...plans, newPlan]);
      setSuccessMessage(`Plan "${newPlan.name}" has been created successfully.`);
    }
    
    setOpenPlanDialog(false);
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };
  
  // Handle opening the delete dialog
  const handleOpenDeleteDialog = (plan) => {
    setCurrentPlan(plan);
    setOpenDeleteDialog(true);
  };
  
  // Handle deleting a plan
  const handleDeletePlan = () => {
    setPlans(plans.filter(p => p.id !== currentPlan.id));
    setOpenDeleteDialog(false);
    setSuccessMessage(`Plan "${currentPlan.name}" has been deleted successfully.`);
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };
  
  // Handle opening the features dialog
  const handleOpenFeaturesDialog = (plan) => {
    setCurrentPlan(plan);
    setOpenFeaturesDialog(true);
  };
  
  // Handle toggling a feature for a plan
  const handleToggleFeature = (featureId) => {
    const updatedFeatures = currentPlan.features.map(feature => 
      feature.id === featureId ? { ...feature, included: !feature.included } : feature
    );
    
    setCurrentPlan({ ...currentPlan, features: updatedFeatures });
  };
  
  // Handle updating a feature value
  const handleFeatureValueChange = (featureId, value) => {
    const updatedFeatures = currentPlan.features.map(feature => 
      feature.id === featureId ? { ...feature, value } : feature
    );
    
    setCurrentPlan({ ...currentPlan, features: updatedFeatures });
  };
  
  // Handle toggling a plan's active status
  const handleToggleActive = (planId) => {
    setPlans(plans.map(plan => 
      plan.id === planId ? { ...plan, active: !plan.active } : plan
    ));
  };
  
  // Handle input changes for the plan form
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentPlan({
      ...currentPlan,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Subscription Plans</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenPlanDialog()}
        >
          Add New Plan
        </Button>
      </Box>
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}
      
      {/* Plan Summary */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Plan Overview</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Plan Name</TableCell>
                <TableCell align="right">Price (Monthly)</TableCell>
                <TableCell align="right">Price (Annual)</TableCell>
                <TableCell align="right">Users</TableCell>
                <TableCell align="right">Revenue</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => (
                <TableRow key={plan.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {plan.name}
                      {plan.isPopular && (
                        <Chip 
                          label="Popular" 
                          size="small" 
                          color="primary"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">${plan.monthlyPrice}</TableCell>
                  <TableCell align="right">${plan.annualPrice}</TableCell>
                  <TableCell align="right">{plan.users}</TableCell>
                  <TableCell align="right">${plan.revenue.toLocaleString()}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={plan.active ? "Active" : "Inactive"} 
                      color={plan.active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenPlanDialog(plan)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenFeaturesDialog(plan)}
                      sx={{ mr: 1 }}
                    >
                      <BarChartIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenDeleteDialog(plan)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Plan Cards */}
      <Typography variant="h6" gutterBottom>Plan Details</Typography>
      <Grid container spacing={3}>
        {plans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.id}>
            <Card variant={plan.isPopular ? "elevation" : "outlined"} elevation={plan.isPopular ? 6 : 0}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5">{plan.name}</Typography>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={plan.active} 
                        onChange={() => handleToggleActive(plan.id)} 
                        size="small"
                      />
                    }
                    label={plan.active ? "Active" : "Inactive"}
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {plan.description}
                </Typography>
                
                <Box sx={{ my: 2 }}>
                  <Typography variant="h4" component="div">
                    ${plan.monthlyPrice}
                    <Typography variant="caption" sx={{ ml: 1 }}>
                      /month
                    </Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ${plan.annualPrice}/year (Save ${(plan.monthlyPrice * 12) - plan.annualPrice})
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <List dense>
                  {plan.features.filter(f => f.included).map((feature) => (
                    <ListItem key={feature.id} disableGutters>
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                      <ListItemText 
                        primary={`${feature.name}: ${feature.value}`} 
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                <Button 
                  size="small" 
                  onClick={() => handleOpenFeaturesDialog(plan)}
                >
                  Manage Features
                </Button>
                <Button 
                  size="small"
                  variant="outlined"
                  onClick={() => handleOpenPlanDialog(plan)}
                >
                  Edit Plan
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {/* Add/Edit Plan Dialog */}
      <Dialog 
        open={openPlanDialog} 
        onClose={() => setOpenPlanDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {editMode ? `Edit ${currentPlan?.name} Plan` : 'Add New Plan'}
            </Typography>
            <IconButton onClick={() => setOpenPlanDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Plan Name"
                name="name"
                value={currentPlan?.name || ''}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={currentPlan?.description || ''}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Monthly Price ($)"
                name="monthlyPrice"
                type="number"
                value={currentPlan?.monthlyPrice || ''}
                onChange={handleInputChange}
                fullWidth
                required
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Annual Price ($)"
                name="annualPrice"
                type="number"
                value={currentPlan?.annualPrice || ''}
                onChange={handleInputChange}
                fullWidth
                required
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={currentPlan?.isPopular || false} 
                    onChange={handleInputChange} 
                    name="isPopular"
                  />
                }
                label="Mark as Popular"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={currentPlan?.active || false} 
                    onChange={handleInputChange} 
                    name="active"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPlanDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSavePlan}
            variant="contained"
            disabled={!currentPlan?.name || !currentPlan?.monthlyPrice || !currentPlan?.annualPrice}
          >
            {editMode ? 'Update Plan' : 'Create Plan'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Plan Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the "{currentPlan?.name}" plan? This action cannot be undone.
          </Typography>
          {currentPlan?.users > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This plan currently has {currentPlan.users} active users. They will need to be migrated to another plan.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeletePlan} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Manage Features Dialog */}
      <Dialog
        open={openFeaturesDialog}
        onClose={() => setOpenFeaturesDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Manage Features for {currentPlan?.name} Plan
            </Typography>
            <IconButton onClick={() => setOpenFeaturesDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Feature</TableCell>
                  <TableCell>Included</TableCell>
                  <TableCell>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableFeatures.map((feature) => {
                  const planFeature = currentPlan?.features?.find(f => f.id === feature.id) || 
                    { id: feature.id, name: feature.name, included: false, value: '' };
                  
                  return (
                    <TableRow key={feature.id}>
                      <TableCell>{feature.name}</TableCell>
                      <TableCell>
                        <Switch 
                          checked={planFeature.included} 
                          onChange={() => handleToggleFeature(feature.id)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={planFeature.value || ''}
                          onChange={(e) => handleFeatureValueChange(feature.id, e.target.value)}
                          disabled={!planFeature.included}
                          size="small"
                          placeholder={feature.type === 'boolean' ? 'Yes/No' : 'Specify value'}
                          fullWidth
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFeaturesDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              setPlans(plans.map(p => p.id === currentPlan.id ? { ...p, features: currentPlan.features } : p));
              setOpenFeaturesDialog(false);
              setSuccessMessage(`Features for "${currentPlan.name}" plan have been updated successfully.`);
              
              // Clear success message after 3 seconds
              setTimeout(() => {
                setSuccessMessage('');
              }, 3000);
            }}
            variant="contained"
          >
            Save Features
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminPlans;
